import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, CheckCircle, TrendingUp, Gauge, Plus, BookOpen, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { useAnalytics } from '../hooks/useAnalytics';
import { formatNumber, formatPercent } from '../lib/utils';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { DashboardStats, DailyStats } from '../types';

// Mock data for demo
const mockDashboardStats: DashboardStats = {
  total_sites: 3,
  total_verifications: 12543,
  verifications_today: 342,
  verifications_week: 2156,
  verifications_month: 8934,
  success_rate: 98.5,
  monthly_limit: 10000,
  usage_percentage: 89.3,
};

const mockDailyStats: DailyStats[] = [
  { date: '2026-02-16', total: 280, successful: 275, failed: 5 },
  { date: '2026-02-17', total: 320, successful: 315, failed: 5 },
  { date: '2026-02-18', total: 290, successful: 285, failed: 5 },
  { date: '2026-02-19', total: 350, successful: 345, failed: 5 },
  { date: '2026-02-20', total: 310, successful: 305, failed: 5 },
  { date: '2026-02-21', total: 340, successful: 335, failed: 5 },
  { date: '2026-02-22', total: 342, successful: 337, failed: 5 },
];

const mockRecentVerifications = [
  { id: '1', site: 'example.com', status: 'success', time: '2 min ago' },
  { id: '2', site: 'mysite.com', status: 'success', time: '5 min ago' },
  { id: '3', site: 'shop.com', status: 'failed', time: '8 min ago' },
  { id: '4', site: 'example.com', status: 'success', time: '12 min ago' },
  { id: '5', site: 'mysite.com', status: 'success', time: '15 min ago' },
];

export function DashboardPage() {
  const { fetchDashboardStats, loading, error } = useAnalytics();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [chartData, setChartData] = useState<DailyStats[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchDashboardStats();
        if (data) {
          setStats(data);
        } else {
          // Use mock data for demo
          setStats(mockDashboardStats);
          setChartData(mockDailyStats);
        }
      } catch {
        // Use mock data for demo
        setStats(mockDashboardStats);
        setChartData(mockDailyStats);
      }
    };
    loadData();
  }, [fetchDashboardStats]);

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const displayStats = stats || mockDashboardStats;
  const displayChartData = chartData.length > 0 ? chartData : mockDailyStats;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">
            Overview of your CheckHumans activity
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/docs">
              <BookOpen className="mr-2 h-4 w-4" />
              Documentation
            </Link>
          </Button>
          <Button asChild>
            <Link to="/sites">
              <Plus className="mr-2 h-4 w-4" />
              Add Site
            </Link>
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 text-sm text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 rounded-md">
          <AlertCircle className="h-4 w-4" />
          Using demo data. Connect to API for real data.
        </div>
      )}

      {/* Stats cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Sites</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{displayStats.total_sites}</div>
            <p className="text-xs text-muted-foreground">
              Active sites
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Today's Verifications</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(displayStats.verifications_today)}</div>
            <p className="text-xs text-muted-foreground">
              +12% from yesterday
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">{formatPercent(displayStats.success_rate)}</div>
            <p className="text-xs text-muted-foreground">
              Last 7 days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Usage</CardTitle>
            <Gauge className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPercent(displayStats.usage_percentage)}</div>
            <Progress value={displayStats.usage_percentage} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-1">
              {formatNumber(displayStats.verifications_month)} / {formatNumber(displayStats.monthly_limit)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts section */}
      <div className="grid gap-4 lg:grid-cols-7">
        {/* Verification chart */}
        <Card className="lg:col-span-4">
          <CardHeader>
            <CardTitle>Weekly Verifications</CardTitle>
            <CardDescription>
              Verification trends over the last 7 days
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={displayChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}
                    className="text-xs"
                  />
                  <YAxis className="text-xs" />
                  <Tooltip
                    labelFormatter={(value) => new Date(value).toLocaleDateString('en-US')}
                    formatter={(value: number, name: string) => [
                      formatNumber(value),
                      name === 'successful' ? 'Successful' : name === 'failed' ? 'Failed' : 'Total'
                    ]}
                    contentStyle={{ backgroundColor: 'hsl(var(--background))', borderColor: 'hsl(var(--border))' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="successful"
                    stroke="#10B981"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="failed"
                    stroke="#EF4444"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Recent verifications */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle>Recent Verifications</CardTitle>
            <CardDescription>
              Latest activity on your sites
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {mockRecentVerifications.map((item) => (
                <div key={item.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-2 w-2 rounded-full ${item.status === 'success' ? 'bg-success' : 'bg-error'}`} />
                    <div>
                      <p className="text-sm font-medium">{item.site}</p>
                      <p className="text-xs text-muted-foreground">{item.time}</p>
                    </div>
                  </div>
                  <Badge variant={item.status === 'success' ? 'success' : 'destructive'}>
                    {item.status === 'success' ? 'Success' : 'Failed'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick actions */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.location.href = '/sites'}>
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Plus className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">Add Site</h3>
              <p className="text-sm text-muted-foreground">Set up CAPTCHA for a new website</p>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.location.href = '/analytics'}>
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="p-3 bg-success/10 rounded-lg">
              <TrendingUp className="h-6 w-6 text-success" />
            </div>
            <div>
              <h3 className="font-semibold">View Analytics</h3>
              <p className="text-sm text-muted-foreground">Detailed verification statistics</p>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <BookOpen className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <h3 className="font-semibold">Documentation</h3>
              <p className="text-sm text-muted-foreground">API & integration guides</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
